import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

import { InterviewType } from '@common/ts/enums';
import { getErrorMessage } from '@common/utils/getErrorDetails';

import { ERROR_MESSAGES } from '@common/constants';
import useChatStore from '../store';
import { iframeLocalStorage } from '../utils/iframeLocalStorage';
import { getLanguageCode } from '../utils/getLanguageCode';
import useChatSettingsStore from '../ChatSettingsStore';
import { translations } from '../translations/chatInputTranslations';
import { getRespondentUiTranslation } from '../translations/respondentUiTranslations';
import { NetworkType, TimeoutProfile } from '../enums';
import { AbortTimeoutConfig } from '../types';
import { SSEParser } from '../utils/sseParser';

interface TSendMessageProps {
  file?: Blob | null;
  text: string;
  onError: (error: Error) => void;
  onSuccess?: () => void;
}

const useServerMessageHandler = (projectStorageSuffix?: string) => {
  const {
    setSeen,
    setReceived,
    setInputsDisabled,
    setStatusOnLastMessage,
    updateLastMessage,
    refreshChatHistory,
    refreshTimeout,
    refreshMessage,
  } = useChatStore();
  const { chatSettingsData } = useChatSettingsStore();
  const isTextChat = chatSettingsData?.includes(InterviewType.TEXT);

  const messageHandler = async (messageData: TResponseHandlerProps, timeout: NodeJS.Timeout) => {
    const {
      data: { message },
      event,
    } = messageData;

    clearTimeout(timeout);
    if (event === 'SEEN') {
      return setSeen();
    }
    if (event === 'STATUS') {
      return setStatusOnLastMessage(message);
    }
    if (event === 'TRANSCODING_VIDEO') {
      const storageKey = `current_project_language_code${projectStorageSuffix}`;
      const lang = storageKey ? iframeLocalStorage.getItem(storageKey) || 'en' : 'en';
      const t = translations[lang] || translations.en;
      return setStatusOnLastMessage(t.processingVideo);
    }
    if (event === 'MESSAGE') {
      updateLastMessage(message);
      iframeLocalStorage.removeItem('last_user_message');
      return setInputsDisabled(false);
    }
    if (event === 'MESSAGE_RECEIVED') {
      return setReceived();
    }
    if (event === 'TIMEOUT') {
      await refreshChatHistory?.();
      const ui = getRespondentUiTranslation(getLanguageCode(projectStorageSuffix));
      toast.error(ui.messageProcessingError, {
        autoClose: 10000,
      });
      return refreshTimeout();
    }
    if (event === 'NO_SPEECH_DETECTED') {
      const ui = getRespondentUiTranslation(getLanguageCode(projectStorageSuffix));
      const message = isTextChat ? ui.microphoneProblemTextChat : ui.microphoneProblemVoiceChat;

      toast.error(message, {
        autoClose: 10000,
      });

      const messages = [...useChatStore.getState().data.messages];

      if (messages.length > 0) {
        // And also remove the assistant loading message if it exists
        if (messages.length >= 2 && messages[messages.length - 2].role === 'user') {
          // find the last user message and remove, and set new field called hasSpeech
          messages[messages.length - 2].hasSpeech = false;
        }

        if (
          messages.length > 0 &&
          messages[messages.length - 1].role === 'assistant' &&
          messages[messages.length - 1].loading
        ) {
          messages.pop();
        }

        useChatStore.setState({
          data: {
            ...useChatStore.getState().data,
            messages,
          },
        });
      }

      setInputsDisabled(false);
      // eslint-disable-next-line consistent-return
      return;
    }
    if (event === 'ERROR') {
      refreshMessage(message);
      return setInputsDisabled(false);
    }
    return null;
  };

  return messageHandler;
};

const useSendMessage = (projectStorageSuffix?: string) => {
  const messageHandler = useServerMessageHandler(projectStorageSuffix);
  const { refreshChatHistory, setInputsDisabled } = useChatStore((state) => state);

  const TIMEOUTS = {
    main: {
      [TimeoutProfile.MobileLarge]: 60000,
      [TimeoutProfile.Mobile]: 40000,
      [TimeoutProfile.Desktop]: 30000,
    },
    initial: {
      [TimeoutProfile.MobileLarge]: 30000,
      [TimeoutProfile.Mobile]: 20000,
      [TimeoutProfile.Desktop]: 15000,
    },
  };

  const getTimeoutProfile = (isMobile: boolean, hasLargeFile: boolean): TimeoutProfile => {
    if (isMobile && hasLargeFile) return TimeoutProfile.MobileLarge;
    if (isMobile) return TimeoutProfile.Mobile;
    return TimeoutProfile.Desktop;
  };

  const calculateTimeoutDurations = (isMobile: boolean, hasLargeFile: boolean) => {
    const profile = getTimeoutProfile(isMobile, hasLargeFile);
    return {
      mainTimeoutDuration: TIMEOUTS.main[profile],
      initialTimeoutDuration: TIMEOUTS.initial[profile],
    };
  };

  // Base timeout covers text-only messages + AI response generation.
  // File uploads and slow networks add on top via adjustments below.
  const BASE_ABORT_TIMEOUT = 90000;
  const FileSizeAdjustments = {
    Mobile: {
      perMB: 15000,
      max: 300000,
    },
    Desktop: {
      perMB: 10000,
      max: 240000,
    },
  };

  const NetworkAdjustments: Record<NetworkType, number> = {
    [NetworkType._2G]: 120000,
    [NetworkType._3G]: 60000,
    [NetworkType._4G]: 30000,
    [NetworkType.Unknown]: 15000,
  };

  // Utility to determine file size adjustment
  const calculateFileSizeAdjustment = (
    file: Blob | null | undefined,
    isMobile: boolean,
  ): number => {
    if (!file) return 0;
    const profile = isMobile ? 'Mobile' : 'Desktop';
    const { perMB, max } = FileSizeAdjustments[profile];
    const fileSizeMB = file.size / (1024 * 1024);
    return Math.min(fileSizeMB * perMB, max);
  };

  // Utility to determine network adjustment
  const calculateNetworkAdjustment = (): number => {
    const { connection } = navigator as Navigator & { connection?: { effectiveType?: string } };
    if (!connection) return 0;
    const effectiveType = (connection.effectiveType as NetworkType) || NetworkType.Unknown;
    return NetworkAdjustments[effectiveType] ?? 0;
  };

  // Main timeout calculation
  const getAbortTimeoutDuration = ({ file, isMobile }: AbortTimeoutConfig): number => {
    return (
      BASE_ABORT_TIMEOUT +
      calculateFileSizeAdjustment(file, isMobile) +
      calculateNetworkAdjustment()
    );
  };

  const handleHistoryError = (error: AxiosError) => {
    const message = getErrorMessage(error);
    toast.error(message);
  };

  const sendMessage = async ({ file, text, onError, onSuccess }: TSendMessageProps) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasLargeFile = !!(file && file.size > 1024 * 1024 * 5); // 5MB

    const { mainTimeoutDuration, initialTimeoutDuration } = calculateTimeoutDurations(
      isMobile,
      hasLargeFile,
    );

    // Apply network and file-size adjustments to all timeouts, not just abortTimeout
    const networkAdjustment = calculateNetworkAdjustment();
    const fileSizeAdjustment = calculateFileSizeAdjustment(file, isMobile);
    const adjustedMainTimeout = mainTimeoutDuration + networkAdjustment + fileSizeAdjustment;
    const adjustedInitialTimeout = initialTimeoutDuration + networkAdjustment + fileSizeAdjustment;

    const controller = new AbortController();

    const timeoutId = setTimeout(async () => {
      if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
      controller.abort();
      setInputsDisabled(false);
      toast.error(getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).responseTimeoutError);

      try {
        await refreshChatHistory?.();
      } catch (error) {
        handleHistoryError(error);
      }
    }, adjustedMainTimeout);

    let thisRequestReceivedResponse = false;
    let initialResponseTimeout: NodeJS.Timeout | null = null;

    const formdata = new FormData();
    if (file) {
      formdata.append('file', file);
    }
    formdata.append('text', text);

    try {
      // Calculate dynamic timeout based on file size and network conditions
      const timeoutDuration = getAbortTimeoutDuration({
        file,
        isMobile,
      });

      const abortTimeout = setTimeout(() => controller.abort(), timeoutDuration);

      const conversationId = useChatStore.getState().currentConversationId;
      const sse = await fetch(`${import.meta.env.VITE_CHAT_URL}/chat/${conversationId}`, {
        method: 'PUT',
        redirect: 'follow',
        body: formdata,
        signal: controller.signal,
      });

      // Start initialResponseTimeout only after upload completes (response headers arrived).
      // Set on every request (not guarded by hasReceivedFirstResponse) so subsequent
      // messages also benefit from the fast-fail timeout.
      initialResponseTimeout = setTimeout(async () => {
        if (!thisRequestReceivedResponse) {
          clearTimeout(timeoutId);
          clearTimeout(abortTimeout);
          controller.abort();
          setInputsDisabled(false);
          toast.error(getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).noResponseError);
          try {
            await refreshChatHistory?.();
          } catch (error) {
            handleHistoryError(error);
          }
        }
      }, adjustedInitialTimeout);

      if (sse.body && sse.body.getReader) {
        const reader = sse.body.getReader();
        const sseParser = new SSEParser();

        const readChunk = async () => {
          try {
            const { done, value } = await reader.read();
            if (done) {
              sseParser.reset();
              reader.releaseLock();
              clearTimeout(timeoutId);
              if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
              clearTimeout(abortTimeout);
              return;
            }

            const messages = sseParser.parseChunk(value);
            for (const message of messages) {
              thisRequestReceivedResponse = true;
              if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
              if ((message as TResponseHandlerProps).event === 'MESSAGE') {
                onSuccess?.();
              }
              messageHandler(message as TResponseHandlerProps, timeoutId);
            }

            readChunk();
          } catch (readError) {
            sseParser.reset();
            if (readError instanceof DOMException && readError.name === 'AbortError') {
              return;
            }
            console.error('Error reading from stream:', readError);
            reader.releaseLock();
            clearTimeout(abortTimeout);
            if (initialResponseTimeout) clearTimeout(initialResponseTimeout);

            // Auto-recovery: fetch latest state from MongoDB, then check if
            // the AI already completed its response server-side.
            try {
              await refreshChatHistory?.();
            } catch (error) {
              handleHistoryError(error);
            }

            const { messages } = useChatStore.getState().data;
            const lastMsg = messages[messages.length - 1];
            const aiAlreadyResponded = lastMsg?.role === 'assistant' && !lastMsg.loading;

            if (!aiAlreadyResponded) {
              toast.info(
                getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).connectionInterrupted,
                { autoClose: 8000 },
              );
            }
            setInputsDisabled(false);
          }
        };

        readChunk();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      Sentry.captureException(error);
      onError(error as AxiosError);
    }
  };

  const onSendMessageErrorHandler = (error: unknown) => {
    const axiosError = error as AxiosError;
    let message = getErrorMessage(axiosError);

    // Check for TypeError: Failed to fetch
    if (error instanceof TypeError && error.message === ERROR_MESSAGES.FAILED_TO_FETCH) {
      message = ERROR_MESSAGES.FAILED_TO_SEND_MESSAGE_WHILE_NETWORK_ERROR;
    }

    toast.error(message);
    setInputsDisabled(false);
  };

  return { sendMessage, onSendMessageErrorHandler };
};

export default useSendMessage;
